import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ChevronDown, Zap, Play } from 'lucide-react';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'Features', path: '/features' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Success Stories', path: '/success-stories' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  const handleStartTrial = () => {
    alert('30-Day Risk-Free Trial signup - will integrate with authentication system');
  };

  const handleWatchDemo = () => {
    alert('2-Minute Demo video - will integrate with video player or Loom');
  };

  // Enhanced navigation with scroll to top
  const handleNavClick = (path, e) => {
    e.preventDefault();
    setIsOpen(false);
    
    // If already on the page, scroll to top smoothly
    if (location.pathname === path) {
      window.scrollTo({ 
        top: 0, 
        behavior: 'smooth' 
      });
    } else {
      // Navigate to new page
      navigate(path);
      // Scroll to top after navigation
      setTimeout(() => {
        window.scrollTo({ 
          top: 0, 
          behavior: 'smooth' 
        });
      }, 100);
    }
  };

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-700 ${
      isScrolled ? 'bg-brand-ivory/95 backdrop-blur-xl shadow-xl border-b border-brand-charcoal/20' : 'bg-brand-charcoal/90 backdrop-blur-[10px]'
    }`} style={!isScrolled ? { backdropFilter: 'blur(10px)' } : {}}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Enhanced Logo with Advanced Animation */}
          <Link 
            to="/" 
            onClick={(e) => handleNavClick('/', e)}
            className="flex items-center space-x-3 group"
          >
            <div className="relative">
              {/* Main logo with multiple animation layers - Increased size */}
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center transform group-hover:rotate-12 transition-all duration-500 shadow-lg group-hover:shadow-brand-teal/50 p-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_prismospark-resto/artifacts/759vyyk9_prismospark-2.png" 
                  alt="PrismoSpark Logo" 
                  className="w-full h-full object-contain transform group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              {/* Animated glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-sienna-accent to-sienna-accent-600 rounded-xl opacity-0 group-hover:opacity-30 blur-md transition-all duration-500 -z-10 scale-110"></div>
              {/* Rotating ring effect */}
              <div className="absolute inset-0 border-2 border-sienna-accent rounded-xl opacity-0 group-hover:opacity-100 animate-spin-slow transition-opacity duration-500"></div>
            </div>
            <span className={`text-2xl font-bold transition-all duration-500 ${
              isScrolled ? 'text-brand-charcoal' : 'text-white'
            } group-hover:bg-gradient-to-r group-hover:from-sienna-accent group-hover:to-sienna-accent-600 group-hover:bg-clip-text group-hover:text-transparent transform group-hover:scale-105`}
            style={!isScrolled ? { textShadow: '0 2px 4px rgba(0,0,0,0.5)' } : {}}>
              PrismoSpark
            </span>
          </Link>

          {/* Enhanced Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-0.5">
            {navItems.map((item, index) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={(e) => handleNavClick(item.path, e)}
                className={`relative px-2 py-2 text-sm font-medium transition-all duration-500 rounded-lg group overflow-hidden whitespace-nowrap ${
                  location.pathname === item.path 
                    ? (isScrolled ? 'text-forest-primary' : 'text-white') 
                    : (isScrolled ? 'text-brand-charcoal-700 hover:text-sienna-accent' : 'text-white hover:text-sienna-accent')
                }`}
                style={!isScrolled ? { textShadow: '0 2px 4px rgba(0,0,0,0.5)' } : {}}
              >
                {/* Text with enhanced animation */}
                <span className="relative z-10 transform group-hover:scale-105 transition-transform duration-300">{item.name}</span>
                
                {/* Active state background */}
                {location.pathname === item.path && (
                  <div className="absolute inset-0 bg-gradient-to-r from-forest-primary/20 to-forest-primary-600/20 rounded-lg border border-forest-primary/30 animate-pulse-subtle"></div>
                )}
                
                {/* Hover effect background */}
                <div className="absolute inset-0 bg-gradient-to-r from-sienna-accent/10 to-sienna-accent-600/10 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-500 transform scale-75 group-hover:scale-100"></div>
                
                {/* Animated underline effect */}
                <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-sienna-accent to-sienna-accent-600 group-hover:w-full transition-all duration-500"></div>
                
                {/* Sparkle effect on hover */}
                <div className="absolute top-1 right-1 w-1 h-1 bg-sienna-accent rounded-full opacity-0 group-hover:opacity-100 animate-ping transition-opacity duration-300"></div>
              </Link>
            ))}
          </div>

          {/* Modern CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-3">
            <Button 
              onClick={handleWatchDemo}
              className={`group relative font-medium px-5 py-2.5 rounded-xl border-2 transition-all duration-300 transform hover:scale-105 overflow-hidden ${
                isScrolled 
                  ? 'border-brand-charcoal-200 bg-brand-ivory text-brand-charcoal-700 hover:bg-white hover:shadow-lg' 
                  : 'border-white/20 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 hover:border-white/40'
              }`}
              style={!isScrolled ? { textShadow: '0 2px 4px rgba(0,0,0,0.5)' } : {}}
            >
              <div className="relative z-10 flex items-center space-x-2" style={!isScrolled ? { textShadow: '0 2px 4px rgba(0,0,0,0.5)' } : {}}>
                <Play size={14} className="group-hover:scale-110 transition-transform duration-300" />
                <span>Watch Demo</span>
              </div>
              {/* Shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
            </Button>
            
            <Button 
              onClick={handleStartTrial}
              className="group relative bg-gradient-to-br from-sienna-accent to-sienna-accent-600 hover:from-sienna-accent-600 hover:to-sienna-accent-700 text-white font-semibold px-6 py-2.5 rounded-xl shadow-lg hover:shadow-sienna-accent/25 transition-all duration-300 transform hover:scale-105 hover:-translate-y-0.5 border-0 overflow-hidden"
            >
              {/* Animated glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              {/* Content */}
              <div className="relative z-10 flex items-center space-x-2" style={!isScrolled ? { textShadow: '0 2px 4px rgba(0,0,0,0.5)' } : {}}>
                <Zap size={16} className="group-hover:rotate-12 group-hover:scale-110 transition-all duration-300" />
                <span>Start Trial</span>
              </div>
              
              {/* Shimmer sweep */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-in-out"></div>
              
              {/* Outer glow */}
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-sienna-accent/50 to-sienna-accent-600/50 blur-lg opacity-0 group-hover:opacity-60 transition-opacity duration-500 -z-10 scale-110"></div>
            </Button>
          </div>

          {/* Compact navigation for medium screens */}
          <div className="hidden md:flex lg:hidden items-center space-x-2">
            {navItems.slice(0, 6).map((item, index) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={(e) => handleNavClick(item.path, e)}
                className={`relative px-2 py-2 text-xs font-medium transition-all duration-500 rounded-lg group overflow-hidden ${
                  location.pathname === item.path 
                    ? (isScrolled ? 'text-forest-primary' : 'text-white') 
                    : (isScrolled ? 'text-slate-700 hover:text-sienna-accent' : 'text-white hover:text-sienna-accent')
                }`}
                style={!isScrolled ? { textShadow: '0 2px 4px rgba(0,0,0,0.5)' } : {}}
              >
                <span className="relative z-10 transform group-hover:scale-105 transition-transform duration-300">{item.name}</span>
                
                {/* Active state background */}
                {location.pathname === item.path && (
                  <div className="absolute inset-0 bg-gradient-to-r from-forest-primary/20 to-forest-primary-600/20 rounded-lg border border-forest-primary/30 animate-pulse-subtle"></div>
                )}
                
                {/* Hover effect background */}
                <div className="absolute inset-0 bg-gradient-to-r from-sienna-accent/10 to-sienna-accent-600/10 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-500 transform scale-75 group-hover:scale-100"></div>
                
                {/* Animated underline effect */}
                <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-sienna-accent to-sienna-accent-600 group-hover:w-full transition-all duration-500"></div>
              </Link>
            ))}
            {/* More button for additional items */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`relative px-2 py-2 text-xs font-medium transition-all duration-500 rounded-lg group overflow-hidden ${
                isScrolled ? 'text-slate-700 hover:text-sienna-accent' : 'text-white hover:text-sienna-accent'
              }`}
              style={!isScrolled ? { textShadow: '0 2px 4px rgba(0,0,0,0.5)' } : {}}
            >
              <span className="relative z-10">More</span>
              <ChevronDown size={12} className="inline ml-1" />
            </button>
          </div>

          {/* Enhanced Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`p-3 rounded-xl transition-all duration-500 transform hover:scale-110 ${
                isScrolled ? 'text-brand-charcoal-600 hover:text-brand-charcoal-900 hover:bg-brand-ivory' : 'text-white hover:text-white hover:bg-white/10'
              } ${isOpen ? 'rotate-180' : 'rotate-0'}`}
              style={!isScrolled ? { textShadow: '0 2px 4px rgba(0,0,0,0.5)' } : {}}
            >
              <div className="relative">
                {isOpen ? <X size={24} /> : <Menu size={24} />}
                {/* Animated glow for mobile button */}
                <div className={`absolute inset-0 rounded-xl transition-all duration-300 ${
                  isOpen ? 'bg-red-400/20 blur-md' : 'bg-violet-400/0 blur-none'
                }`}></div>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Navigation - SOLID Background */}
        {isOpen && (
          <div 
            className="md:hidden absolute top-full left-0 right-0 w-full shadow-2xl animate-slide-down"
            style={{ 
              backgroundColor: 'rgb(43, 45, 47)',
              zIndex: 1001
            }}
          >
            <div className="px-0 py-4">
              {navItems.map((item, index) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={(e) => handleNavClick(item.path, e)}
                  className={`block w-full px-5 py-4 text-base font-medium text-white transition-all duration-300 ${
                    location.pathname === item.path 
                      ? 'bg-sienna-accent/20 border-l-4 border-sienna-accent' 
                      : 'hover:bg-white/10'
                  }`}
                  style={{
                    minHeight: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                    animationDelay: `${index * 100}ms`,
                    animation: `fade-in-left 0.5s ease-out ${index * 100}ms both`
                  }}
                >
                  <div className="flex items-center justify-between w-full">
                    <span>{item.name}</span>
                    {location.pathname === item.path && (
                      <div className="w-2 h-2 bg-sienna-accent rounded-full animate-pulse"></div>
                    )}
                  </div>
                </Link>
              ))}
              
              <div className="px-5 pt-6 pb-4 space-y-4">
                <Button 
                  onClick={() => {
                    handleWatchDemo();
                    setIsOpen(false);
                  }}
                  className="w-full border-2 border-white/30 bg-white/10 text-white hover:bg-white/20 hover:border-white/50 font-medium py-4 rounded-xl transition-all duration-300"
                  style={{
                    minHeight: '44px',
                    textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                  }}
                >
                  <Play size={16} className="mr-2" />
                  Watch Demo
                </Button>
                
                <Button 
                  onClick={() => {
                    handleStartTrial();
                    setIsOpen(false);
                  }}
                  className="w-full bg-gradient-to-br from-sienna-accent to-sienna-accent-600 hover:from-sienna-accent-600 hover:to-sienna-accent-700 text-white font-bold py-4 rounded-xl shadow-xl border-0 transition-all duration-300"
                  style={{
                    minHeight: '44px',
                    textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                  }}
                >
                  <Zap size={16} className="mr-2" />
                  Start Trial
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Enhanced CSS Animations */}
      <style jsx>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes pulse-subtle {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        
        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fade-in-left {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
        
        .animate-pulse-subtle {
          animation: pulse-subtle 2s ease-in-out infinite;
        }
        
        .animate-slide-down {
          animation: slide-down 0.5s ease-out;
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out both;
        }
      `}</style>
    </nav>
  );
};

export default Navigation;